import { Input, Select } from '@/components/ui/FormControls';
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, PencilLine, X } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import {
  calendarDateAllowed,
  calendarDateISO,
  calendarMonth,
  parseCalendarDate,
  parseCalendarEntry,
} from '@klothiq/domain';
import { localBusinessDate } from '@klothiq/validation';
import { cn } from '@/lib/utils';
import { fieldHelpAttributes, type FieldHelpProps } from '@/lib/fieldGuidance';

interface DatePickerProps extends FieldHelpProps, Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'onChange' | 'type'
> {
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  clearable?: boolean;
  min?: string;
  max?: string;
  today?: string;
  hasError?: boolean;
}

const months = Array.from({ length: 12 }, (_, month) =>
  new Intl.DateTimeFormat('en-IN', { month: 'long' }).format(new Date(2026, month, 1)),
);
const displayDate = (date: Date) =>
  new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(
    date,
  );
const actionClass =
  'inline-flex min-h-11 sm:min-h-9 items-center justify-center rounded-xl border border-border px-3 text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40';

function usePhoneLayout() {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );
  useEffect(() => {
    const query = window.matchMedia('(max-width: 639px)');
    const update = () => setPhone(query.matches);
    query.addEventListener('change', update);
    update();
    return () => query.removeEventListener('change', update);
  }, []);
  return phone;
}

/** Date-only values stay in YYYY-MM-DD; selection never passes through UTC. */
const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(function DatePicker(
  {
    value,
    onChange,
    placeholder = 'Select a date',
    clearable = true,
    min,
    max,
    today = localBusinessDate(),
    hasError,
    className,
    disabled,
    help,
    helpLabel,
    'aria-label': label = 'Choose date',
    ...buttonProps
  },
  ref,
) {
  const phone = usePhoneLayout();
  const uid = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [centered, setCentered] = useState(false);
  const [entryMode, setEntryMode] = useState<'calendar' | 'manual'>('calendar');
  const [phoneViewport, setPhoneViewport] = useState<{ height: number; bottom: number }>();
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!open || !phone || !viewport) return;
    const update = () =>
      setPhoneViewport({
        height: viewport.height,
        bottom: Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop),
      });
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, [open, phone]);
  const selected = parseCalendarDate(value);
  const earliest = parseCalendarDate(min);
  const latest = parseCalendarDate(max);
  const todayDate = parseCalendarDate(today) ?? new Date();
  const [month, setMonth] = useState(() => calendarMonth(selected ?? todayDate));
  const [entry, setEntry] = useState('');
  const [entryError, setEntryError] = useState('');

  const setVisible = (next: boolean) => {
    if (next && disabled) return;
    if (next) {
      const bounds = triggerRef.current?.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      // A six-week desktop calendar needs about 450px. Use the whole viewport
      // when neither side of the field can fit it, rather than clipping actions.
      setCentered(!phone && !!bounds && Math.max(bounds.top, viewportHeight - bounds.bottom) < 474);
      setEntryMode('calendar');
      let initial = selected ?? todayDate;
      if (earliest && initial < earliest) initial = earliest;
      if (latest && initial > latest) initial = latest;
      setMonth(calendarMonth(initial));
      setEntry(
        selected
          ? `${String(selected.getDate()).padStart(2, '0')}/${String(selected.getMonth() + 1).padStart(2, '0')}/${String(selected.getFullYear()).padStart(4, '0')}`
          : '',
      );
      setEntryError('');
    }
    setOpen(next);
  };
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const choose = (next: string | undefined) => {
    if (disabled) return;
    if (next && !calendarDateAllowed(next, min, max)) return;
    onChange(next);
    setOpen(false);
  };
  const applyEntry = () => {
    const parsed = parseCalendarEntry(entry);
    if (!parsed) {
      setEntryError('Enter a real date as DD/MM/YYYY or YYYY-MM-DD.');
      return;
    }
    if (!calendarDateAllowed(parsed, min, max)) {
      setEntryError(
        `Choose a date${earliest ? ` on or after ${displayDate(earliest)}` : ''}${latest ? ` on or before ${displayDate(latest)}` : ''}.`,
      );
      return;
    }
    choose(parsed);
  };
  const monthAllowed = (candidate: Date) =>
    candidate.getFullYear() >= 1 &&
    candidate.getFullYear() <= 9999 &&
    (!earliest || calendarMonth(candidate) >= calendarMonth(earliest)) &&
    (!latest || calendarMonth(candidate) <= calendarMonth(latest));
  const startYear =
    earliest?.getFullYear() ??
    Math.max(1, Math.min(todayDate.getFullYear() - 100, month.getFullYear()));
  const endYear =
    latest?.getFullYear() ??
    Math.min(9999, Math.max(todayDate.getFullYear() + 20, month.getFullYear()));
  const jump = (year: number, targetMonth: number) => {
    let next = calendarMonth(month);
    next.setFullYear(year, targetMonth, 1);
    if (earliest && next < calendarMonth(earliest)) next = calendarMonth(earliest);
    if (latest && next > calendarMonth(latest)) next = calendarMonth(latest);
    setMonth(next);
  };
  const trigger = (
    <button
      {...buttonProps}
      {...fieldHelpAttributes({ id: buttonProps.id, label: helpLabel ?? label, type: 'date', min, max }, help)}
      ref={(element) => {
        triggerRef.current = element;
        if (typeof ref === 'function') ref(element);
        else if (ref) ref.current = element;
      }}
      type="button"
      disabled={disabled}
      aria-label={`${helpLabel ?? label}, ${selected ? displayDate(selected) : placeholder}`}
      aria-invalid={hasError || buttonProps['aria-invalid']}
      className={cn(
        'kiq-field',
        'flex min-h-11 w-full items-center gap-2.5 rounded-xl border border-input bg-background px-3 py-2.5 text-left text-sm transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        !selected && 'text-muted-foreground',
        hasError && 'border-destructive',
        className,
      )}
    >
      <CalendarDays aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
      <span className="flex-1 whitespace-nowrap font-medium">
        {selected ? displayDate(selected) : placeholder}
      </span>
      <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
  const panel = (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className={cn(
          'flex shrink-0 items-center justify-between gap-2 border-b border-border bg-primary/5 px-3 py-2',
          phone && 'bg-transparent px-4 py-2.5',
        )}
      >
        {phone && (
          <div
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
          >
            <CalendarDays className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-muted-foreground">
            {phone
              ? label
              : selected
                ? new Intl.DateTimeFormat('en-IN', { weekday: 'long' }).format(selected)
                : 'Pick a date'}
          </p>
          <p className="text-base font-semibold tracking-tight">
            {selected
              ? `${phone ? `${new Intl.DateTimeFormat('en-IN', { weekday: 'short' }).format(selected)}, ` : ''}${displayDate(selected)}`
              : 'Choose a date'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setVisible(false)}
          aria-label="Close date picker"
          className={cn(
            actionClass,
            'w-11 sm:w-9 shrink-0 border-transparent p-0',
            phone && 'rounded-full bg-muted/40',
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <fieldset
        className="mx-3 mb-2 mt-2 flex shrink-0 rounded-xl border border-border bg-muted/30 p-0.5"
        aria-label="Date entry method"
      >
        {(['calendar', 'manual'] as const).map((mode) => (
          <label key={mode} className="relative flex-1 cursor-pointer">
            <Input
              type="radio"
              name={`${uid}-mode`}
              value={mode}
              checked={entryMode === mode}
              onChange={() => setEntryMode(mode)}
              aria-controls={`${uid}-panel`}
              className="peer sr-only"
              helpLabel="Date entry mode"
            />
            <span
              className={cn(
                'flex min-h-11 sm:min-h-9 items-center justify-center gap-2 rounded-lg px-2 text-sm font-medium text-muted-foreground peer-checked:bg-background peer-checked:text-foreground peer-checked:shadow-sm peer-focus-visible:ring-2 peer-focus-visible:ring-ring',
                phone && 'peer-checked:bg-primary/10 peer-checked:text-primary',
              )}
            >
              {phone &&
                (mode === 'calendar' ? (
                  <CalendarDays aria-hidden="true" className="h-4 w-4" />
                ) : (
                  <PencilLine aria-hidden="true" className="h-4 w-4" />
                ))}
              {mode === 'calendar' ? 'Calendar' : 'Enter date'}
            </span>
          </label>
        ))}
      </fieldset>
      {entryMode === 'calendar' ? (
        <div
          id={`${uid}-panel`}
          className="min-h-0 overflow-y-auto overscroll-contain px-1 py-1 sm:px-3"
        >
          <div className="mb-1 flex items-center gap-1 px-1">
            <button
              type="button"
              aria-label="Previous month"
              disabled={!monthAllowed(calendarMonth(month, -1))}
              onClick={() => setMonth(calendarMonth(month, -1))}
              className={cn(
                actionClass,
                'w-11 sm:w-9 shrink-0 border-transparent p-0',
                phone && 'border-border bg-muted/30',
              )}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div
              className={
                phone
                  ? 'flex min-w-0 flex-1 items-center overflow-hidden rounded-xl border border-border bg-muted/30'
                  : 'contents'
              }
            >
              <Select
                aria-label="Month"
                value={month.getMonth()}
                onChange={(event) => jump(month.getFullYear(), Number(event.target.value))}
                className={cn(
                  'min-h-11 sm:min-h-9 min-w-0 flex-1 rounded-xl border border-border bg-background px-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  phone && 'rounded-none border-0 bg-transparent px-3',
                )}
              >
                {months.map((name, index) => {
                  const candidate = calendarMonth(month);
                  candidate.setMonth(index);
                  return (
                    <option key={name} value={index} disabled={!monthAllowed(candidate)}>
                      {name}
                    </option>
                  );
                })}
              </Select>
              <Select
                aria-label="Year"
                value={month.getFullYear()}
                onChange={(event) => jump(Number(event.target.value), month.getMonth())}
                className={cn(
                  'min-h-11 sm:min-h-9 w-[5.5rem] rounded-xl border border-border bg-background px-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  phone && 'w-[4.75rem] rounded-none border-0 border-l bg-transparent',
                )}
              >
                {Array.from(
                  { length: Math.max(0, endYear - startYear + 1) },
                  (_, i) => startYear + i,
                ).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </div>
            <button
              type="button"
              aria-label="Next month"
              disabled={!monthAllowed(calendarMonth(month, 1))}
              onClick={() => setMonth(calendarMonth(month, 1))}
              className={cn(
                actionClass,
                'w-11 sm:w-9 shrink-0 border-transparent p-0',
                phone && 'border-border bg-muted/30',
              )}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <DayPicker
            mode="single"
            month={month}
            onMonthChange={(next) => {
              if (monthAllowed(next)) setMonth(next);
            }}
            selected={selected}
            onSelect={(date) => {
              if (date) choose(calendarDateISO(date));
            }}
            initialFocus
            showOutsideDays
            today={todayDate}
            fromDate={earliest}
            toDate={latest}
            disabled={[
              ...(earliest ? [{ before: earliest }] : []),
              ...(latest ? [{ after: latest }] : []),
            ]}
            classNames={{
              root: 'kiq-calendar',
              months: 'kiq-calendar-months',
              month: 'kiq-calendar-month',
              caption: 'sr-only',
              nav: 'hidden',
              table: 'kiq-calendar-table',
              head_cell: 'kiq-calendar-heading',
              cell: 'kiq-calendar-cell',
              day: 'kiq-calendar-day',
              day_selected: 'kiq-calendar-selected',
              day_today: 'kiq-calendar-today',
              day_outside: 'kiq-calendar-outside',
              day_disabled: 'kiq-calendar-disabled',
            }}
          />
        </div>
      ) : (
        <div
          id={`${uid}-panel`}
          className="min-h-0 space-y-2 overflow-y-auto overscroll-contain px-3 py-3"
        >
          <label
            htmlFor={`${uid}-entry`}
            className="block text-xs font-medium text-muted-foreground"
          >
            Date · DD/MM/YYYY or YYYY-MM-DD
          </label>
          <div className="flex gap-2">
            <Input
              id={`${uid}-entry`}
              autoFocus
              type="text"
              inputMode="text"
              autoComplete="off"
              maxLength={10}
              placeholder="DD/MM/YYYY"
              value={entry}
              onChange={(event) => {
                setEntry(event.target.value);
                setEntryError('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyEntry();
                }
              }}
              aria-invalid={!!entryError}
              aria-describedby={entryError ? `${uid}-error` : undefined}
              className="min-h-11 sm:min-h-9 w-full min-w-0 rounded-xl border border-input bg-background px-3 text-base tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              helpLabel="Date · DD/MM/YYYY or YYYY-MM-DD"
            />
            <button type="button" onClick={applyEntry} className={cn(actionClass, 'bg-secondary')}>
              Apply
            </button>
          </div>
          {entryError && (
            <p
              id={`${uid}-error`}
              role="alert"
              className="text-xs leading-relaxed text-destructive"
            >
              {entryError}
            </p>
          )}
        </div>
      )}
      <div className="flex shrink-0 gap-2 border-t border-border bg-muted/10 px-3 py-2">
        <button
          type="button"
          disabled={!calendarDateAllowed(today, min, max)}
          onClick={() => choose(today)}
          className={cn(
            actionClass,
            'flex-1 border-primary/20 bg-primary/10 text-primary hover:bg-primary/15',
          )}
        >
          Today
        </button>
        {clearable && (
          <button
            type="button"
            disabled={!value}
            onClick={() => choose(undefined)}
            className={cn(actionClass, 'flex-1')}
          >
            Clear date
          </button>
        )}
      </div>
    </div>
  );
  if (phone || centered)
    return (
      <Dialog.Root open={open} onOpenChange={setVisible}>
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm" data-field-guide-scope />
          <Dialog.Content
            aria-describedby={undefined}
            className={cn(
              'fixed z-[81] flex flex-col overflow-hidden border border-border bg-popover text-popover-foreground shadow-2xl outline-none',
              phone
                ? 'kiq-date-sheet'
                : 'left-1/2 top-1/2 w-[340px] max-w-[calc(100vw-24px)] max-h-[calc(100dvh-24px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl',
            )}
            style={
              phone && phoneViewport
                ? ({
                    '--kiq-date-viewport-height': `${phoneViewport.height}px`,
                    '--kiq-date-keyboard-offset': `${phoneViewport.bottom}px`,
                  } as CSSProperties)
                : undefined
            }
          >
            <Dialog.Title className="sr-only">{label}</Dialog.Title>
            {phone && (
              <div
                className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-muted-foreground/30"
                aria-hidden="true"
              />
            )}
            {panel}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  return (
    <Popover.Root open={open} onOpenChange={setVisible} modal>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          aria-label={label}
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="z-[80] flex w-[340px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-2xl border border-border bg-popover text-popover-foreground shadow-2xl outline-none"
          style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}
        >
          {panel}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});

export default DatePicker;
